import { Tooltip } from "@mui/material";
import { DateField, DateInput, InPlaceEditorProps, Labeled, Validator } from "react-admin";
import { AegleInPlaceEditor } from "./AegleInPlaceEditor";
import { toISO, fromISO } from "@/utils/formatters/dateFormatter";

/**
 * In-place editor for **date-only** fields (not timestamps).
 *
 * Displays a formatted date using {@link DateField}, and allows editing via {@link DateInput}.
 * Wrapped in {@link Labeled} to optionally display a label.
 *
 * Not safe for datetime fields: `format={fromISO}` truncates the value to `YYYY-MM-DD` for the
 * input, and `parse={toISO}` ships it back as noon-UTC (`YYYY-MM-DDT12:00:00Z`) to sidestep
 * timezone shift — any time-of-day component on the original value would be lost on edit.
 *
 * @param source - The name of the date field to bind to
 * @param label - Optional label for the field
 * @param readOnly - If true, disables editing and shows display only
 * @param readOnlyReason - Optional tooltip explaining why the field is read-only
 * @param rest - Additional props passed to {@link InPlaceEditor} and {@link DateInput}
 */
const InPlaceDateEditor = ({
  source,
  label,
  readOnly = false,
  readOnlyReason,
  validate,
  ...rest
}: {
  source: string;
  label?: string;
  readOnly?: boolean;
  readOnlyReason?: string;
  validate?: Validator | Validator[];
} & Omit<InPlaceEditorProps, "input" | "source">) => {
  // Read-only mode: just show the display component
  if (readOnly) {
    return (
      <Tooltip title={readOnlyReason}>
        <span>
          <Labeled label={label}>
            <DateField source={source} variant="body1" sx={{ display: "block", mb: 0.5 }} emptyText="-" />
          </Labeled>
        </span>
      </Tooltip>
    );
  }

  return (
    <Labeled label={label}>
      <AegleInPlaceEditor
        source={source}
        editor={
          <DateInput
            {...rest}
            source={source}
            parse={toISO}
            format={fromISO}
            variant="standard"
            size="small"
            margin="none"
            label={false}
            helperText={validate ? undefined : false}
            validate={validate}
            autoFocus
            sx={{
              "& .MuiInput-root": { marginTop: 0 },
            }}
          />
        }
        {...rest}
      >
        <DateField source={source} variant="body1" sx={{ display: "block", mb: 0.5 }} emptyText="-" />
      </AegleInPlaceEditor>
    </Labeled>
  );
};

export default InPlaceDateEditor;
